// What the desktop app adds to the client. In the desktop app, `window.mainionDesktop` is set;
// in a browser it isn't, and the client works as a normal PWA.

import type { Models } from "@mainion/protocol";

export type SetupRequest = {
    /** The server's IP address or host name. */
    host: string;
    port?: number;
    /** Usually root on a fresh server. */
    user: string;
    /** For the first login. Leave empty to use your own SSH keys in ~/.ssh. After setup, the app's own key is used. */
    password?: string;
    /** The password for logging in to mainions from any device, chosen by the user. Only its hash goes to the server. */
    mainionsPassword: string;
    /** The domain name for HTTPS. Leave empty to use <ip>.sslip.io. */
    domain?: string;
    /** A name for the controller, like "vps1". */
    name: string;
    /** The LLM the setup agent uses. */
    llm: { url: string; key?: string; model: string };
};

export type SetupUpdate =
    | { type: "progress"; message: string; done?: number; total?: number }
    | { type: "log"; text: string }
    // The controller runs. Clients connect to `url` with the mainions password.
    | { type: "done"; url: string }
    // The server is in a state the setup agent can't fix. The user should reinstall it and try again.
    | { type: "reset"; reason: string }
    | { type: "failed"; error: string };

export type DesktopApi = {
    /** Sets up a server as a controller. Resolves once the setup ended; the last update says how. */
    setup(request: SetupRequest, onUpdate: (update: SetupUpdate) => void): Promise<void>;
    cancelSetup(): void;
    /** The models from the catalog, like a controller sends them. For setting up the first server, before there is one. */
    models(): Promise<Models>;
};

declare global {
    interface Window {
        mainionDesktop?: DesktopApi;
    }
}

/** The desktop app's extras, or undefined when running as a PWA. */
export const desktop: DesktopApi | undefined = typeof window === "undefined" ? undefined : window.mainionDesktop;
